import React, { useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "../context/WorkspaceContext";
import { useAuth } from "../hooks/useAuth";
import "./WorkspaceSwitcher.css";

const getWorkspaceOptionLabel = (workspace, workspaces) => {
  const normalizedName = String(workspace.nome || "").trim().toLowerCase();
  const hasDuplicateName = workspaces.some(
    (candidate) =>
      candidate.id !== workspace.id &&
      String(candidate.nome || "").trim().toLowerCase() === normalizedName
  );

  if (!hasDuplicateName) {
    return workspace.nome;
  }

  return `${workspace.nome} - ${workspace.papel || "member"}`;
};

const WorkspaceSwitcher = ({ compact = false }) => {
  const navigate = useNavigate();
  const {
    workspaces,
    selectedWorkspaceId,
    setSelectedWorkspaceId,
    loading,
    createWorkspace,
  } = useWorkspace();
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceDescription, setWorkspaceDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleCreateWorkspace = async () => {
    const normalizedName = workspaceName.trim();

    if (!normalizedName) {
      setErrorMessage("Informe o nome do workspace.");
      return;
    }

    if (!user?.id) {
      setErrorMessage("Nao foi possivel identificar o usuario logado para criar o workspace.");
      return;
    }

    setCreating(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const result = await createWorkspace({
        nome: normalizedName,
        descricao: workspaceDescription.trim(),
        idUsuarioCriador: user.id,
      });

      setWorkspaceName("");
      setWorkspaceDescription("");
      setShowModal(false);
      setSuccessMessage(
        result?.recoveredAfterTimeout
          ? "Workspace criado. O backend demorou para responder, mas a criacao foi confirmada na atualizacao da lista."
          : "Workspace criado com sucesso."
      );
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel criar o workspace."
      );
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className={`workspace-switcher ${compact ? "compact" : ""}`}>
        <Spinner size="sm" />
      </div>
    );
  }

  if (workspaces.length === 0) {
    return (
      <div className={`workspace-switcher ${compact ? "compact" : ""}`}>
        <span className="workspace-switcher-empty">Sem workspace</span>
      </div>
    );
  }

  return (
    <>
      {successMessage && (
        <div className="workspace-switcher-feedback">
          <Alert variant="success" dismissible onClose={() => setSuccessMessage("")}>
            {successMessage}
          </Alert>
        </div>
      )}

      <div className={`workspace-switcher ${compact ? "compact" : ""}`}>
        {!compact && <span className="workspace-switcher-label">Workspace</span>}
        <Form.Select
          size={compact ? "sm" : undefined}
          value={selectedWorkspaceId}
          onChange={(event) => {
            if (event.target.value === "__manage_workspaces__") {
              navigate("/perfil/workspaces");
              return;
            }

            setSelectedWorkspaceId(event.target.value);
          }}
        >
          {workspaces.map((workspace) => (
            <option key={workspace.id} value={workspace.id}>
              {getWorkspaceOptionLabel(workspace, workspaces)}
            </option>
          ))}
          <option value="__manage_workspaces__">Gerenciar Workspaces</option>
        </Form.Select>

        <Button
          size={compact ? "sm" : undefined}
          variant="outline-secondary"
          className="workspace-manage-btn"
          onClick={() => navigate("/perfil/workspaces")}
          title="Gerenciar Workspaces"
        >
          <FontAwesomeIcon icon={faGear} />
        </Button>

        <Button
          size={compact ? "sm" : undefined}
          variant="outline-primary"
          className="workspace-create-btn"
          onClick={() => {
            setErrorMessage("");
            setSuccessMessage("");
            setShowModal(true);
          }}
          title="Novo workspace"
        >
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Novo Workspace</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                type="text"
                value={workspaceName}
                onChange={(event) => setWorkspaceName(event.target.value)}
                placeholder="Ex.: Operacoes"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Descricao</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={workspaceDescription}
                onChange={(event) => setWorkspaceDescription(event.target.value)}
                placeholder="Descreva o objetivo do workspace"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleCreateWorkspace} disabled={creating}>
            {creating ? "Criando..." : "Criar Workspace"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WorkspaceSwitcher;
